import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Target,
  User,
  Coins,
  Link2,
} from "lucide-react";
import { useActivityDetail } from "@/hooks/useActivityDetail";
import { useActivityTypes, type ActivityType } from "@/hooks/useActivityTypes";
import LevelBadge from "@/components/level/LevelBadge";
import ShareButton from "@/components/shared/ShareButton";
import ParticipantList from "@/components/activity/ParticipantList";
import ActivityActions from "@/components/activity/ActivityActions";
import OrganizerPanel from "@/components/activity/OrganizerPanel";
import ConnectCourtsDialog from "@/components/activity/ConnectCourtsDialog";
import { formatPrice } from "@/utils/formatPrice";

export default function ActivityDetail() {
  const { t } = useTranslation("activities");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showOrgPanel, setShowOrgPanel] = useState(false);
  const [showConnectCourts, setShowConnectCourts] = useState(false);

  const {
    activity,
    isLoading,
    isOrganizer,
    hasJoined,
    isFull,
    isPast,
    isDeadlinePassed,
    joinChargeMinor,
    hasMitCard,
    hasPaidJoin,
    withinCancellationWindow,
    refundChargeMinor,
    hasPendingJoin,
    pendingChargeMinor,
    chargeEnabled,
    join,
    leave,
    refundLeave,
    completePayment,
    approve,
    reject,
    removePlayer,
    cancelActivity,
    updateActivity,
    isJoining,
    isLeaving,
    isRefundLeaving,
    isCompletingPayment,
    isApproving,
    isRejecting,
    isRemoving,
    isCancelling,
    isUpdating,
  } = useActivityDetail(id);

  const { types: activityTypes } = useActivityTypes();

  if (isLoading) {
    return (
      <div className="space-y-4 pb-20">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="space-y-4 pb-20">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/play")}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> {t("common:back")}
        </Button>
        <p className="text-center py-12 text-muted-foreground">
          {t("detail.notFound")}
        </p>
      </div>
    );
  }

  // Resolve activity type info
  const typeInfo: ActivityType | undefined =
    activity.activity_type
      ? (activityTypes.find((at) => at.id === activity.activity_type?.id) ??
        undefined)
      : activityTypes.find((at) => at.slug === activity.match_type);

  const typeIcon = typeInfo?.icon ?? activity.activity_type?.icon ?? "🎾";
  const typeLabel =
    activity.activity_title ??
    typeInfo?.name ??
    activity.activity_type?.name ??
    activity.match_type;

  const dateStr = format(parseISO(activity.date), "EEEE d MMMM", {
    locale: sv,
  });
  const timeStr = `${activity.start_time?.slice(0, 5)}–${activity.end_time?.slice(0, 5)}`;
  const fillPercent =
    activity.spots_total > 0
      ? Math.round((activity.spots_filled / activity.spots_total) * 100)
      : 0;

  const genderLabel =
    activity.gender_filter === "male"
      ? t("gender.male")
      : activity.gender_filter === "female"
        ? t("gender.female")
        : t("gender.all");

  const paymentMode = activity.payment_mode ?? 'system';

  const priceLabel =
    paymentMode === 'free'
      ? t("activities:interestSignup", "Intresseanmälan")
      : paymentMode === 'external'
        ? activity.display_price != null && activity.display_price > 0
          ? `${formatPrice(activity.display_price)} (${t("activities:externalPaymentShort", "hanteras utanför systemet")})`
          : t("activities:externalPaymentShort", "Pris hanteras utanför systemet")
        // system: show the CANONICAL join charge (computeActivityJoinAmountMinor via the hook —
        // court recomputed from rules + fee), NOT the legacy price_per_person column (null for
        // fee-led activities → it falsely showed "Gratis" while the player is charged court+fee).
        // joinChargeMinor: null = still loading, 0 = genuinely free, >0 = the price. Same source as
        // the card/dialog charge (Lesson 51: charge == display, one source).
        : joinChargeMinor == null
          ? "…"
          : joinChargeMinor > 0
            ? formatPrice(joinChargeMinor / 100)
            : t("free");

  return (
    <div className="space-y-4 pb-20 lg:max-w-5xl lg:mx-auto lg:px-6">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="gap-1"
      >
        <ArrowLeft className="h-4 w-4" /> {t("common:back")}
      </Button>

      {/* Title + type badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{typeLabel}</h1>
            <span className="text-xl">{typeIcon}</span>
          </div>
          {activity.status === "cancelled" && (
            <Badge className="bg-red-500/15 text-red-700" variant="secondary">
              {t("tournament.status.cancelled")}
            </Badge>
          )}
          {activity.visibility === "private" && (
            <Badge variant="outline" className="text-muted-foreground">
              {t("common:private")}
            </Badge>
          )}
        </div>
        <ShareButton
          entityType="open_match"
          entityId={id!}
          title={activity?.activity_title || typeLabel}
          subtitle={t("detail.shareSubtitle", { name: typeLabel })}
        />
      </div>

      {/* Info card */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="capitalize">{dateStr}</span>
            <span className="text-muted-foreground">{timeStr}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
            {paymentMode === 'free' && !activity.court_id ? (
              <span className="text-muted-foreground">{t("activities:noCourtsConnected", "Inga banor kopplade")}</span>
            ) : (
              <span>{activity.courts?.name ?? t("anyCourt")}</span>
            )}
          </div>

          {/* Organizer: connect courts button for free mode */}
          {paymentMode === 'free' && !activity.court_id && isOrganizer && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setShowConnectCourts(true)}
            >
              <Link2 className="h-3.5 w-3.5" />
              {t("activities:connectCourts", "Koppla banor")}
            </Button>
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>
                {t("detail.spotsFilled", {
                  filled: activity.spots_filled,
                  total: activity.spots_total,
                })}
              </span>
            </div>
            <Progress value={fillPercent} className="h-1.5" />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>
              {t("detail.levelRange", {
                min: activity.min_level ?? 1,
                max: activity.max_level ?? 10,
              })}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{genderLabel}</span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Coins className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{priceLabel}</span>
          </div>
        </CardContent>
      </Card>

      {/* Organizer section */}
      {activity.organizer_profile && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("detail.organizer")}
          </p>
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              {activity.organizer_profile.avatar_url && (
                <AvatarImage src={activity.organizer_profile.avatar_url} />
              )}
              <AvatarFallback className="text-xs">
                {(activity.organizer_profile.full_name ?? "?")[0]}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium">
              {activity.organizer_profile.full_name ?? t("common:unknown")}
            </span>
            {activity.organizer_profile.level != null && (
              <LevelBadge level={activity.organizer_profile.level} size="sm" />
            )}
          </div>
        </div>
      )}

      {/* Organizer message */}
      {activity.description && (
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              {t("detail.organizerMessage")}
            </p>
            <p className="text-sm whitespace-pre-wrap">
              {activity.description}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Participant list */}
      <ParticipantList
        players={activity.activity_players}
        organizerId={activity.created_by}
        spotsTotal={activity.spots_total}
        spotsFilled={activity.spots_filled}
        isOrganizer={isOrganizer}
        onApprove={isOrganizer ? approve : undefined}
        onReject={isOrganizer ? reject : undefined}
        onRemove={isOrganizer ? removePlayer : undefined}
        isApproving={isApproving}
        isRejecting={isRejecting}
        isRemoving={isRemoving}
      />

      {/* Action buttons */}
      <ActivityActions
        activity={activity}
        joinChargeMinor={joinChargeMinor}
        hasMitCard={hasMitCard}
        hasPaidJoin={hasPaidJoin}
        withinCancellationWindow={withinCancellationWindow}
        refundChargeMinor={refundChargeMinor}
        hasPendingJoin={hasPendingJoin}
        pendingChargeMinor={pendingChargeMinor}
        chargeEnabled={chargeEnabled}
        isOrganizer={isOrganizer}
        hasJoined={hasJoined}
        isFull={isFull}
        isPast={isPast}
        isDeadlinePassed={isDeadlinePassed}
        onJoin={() => join()}
        onLeave={() => leave()}
        onRefundLeave={() => refundLeave()}
        onManage={() => setShowOrgPanel(!showOrgPanel)}
        onCompletePayment={() => completePayment()}
        isJoining={isJoining}
        isLeaving={isLeaving}
        isRefundLeaving={isRefundLeaving}
        isCompletingPayment={isCompletingPayment}
      />

      {/* Organizer panel */}
      {isOrganizer && showOrgPanel && (
        <OrganizerPanel
          activity={activity}
          players={activity.activity_players}
          onApprove={approve}
          onReject={reject}
          onRemove={removePlayer}
          onCancelActivity={() => cancelActivity()}
          onUpdateActivity={updateActivity}
          onConnectCourts={() => setShowConnectCourts(true)}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isRemoving={isRemoving}
          isCancelling={isCancelling}
          isUpdating={isUpdating}
        />
      )}

      {/* Cancellation policy */}
      {activity.cancellation_hours != null && activity.cancellation_hours > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {t("detail.cancellationPolicy", {
            hours: activity.cancellation_hours,
          })}
        </p>
      )}

      {/* Connect courts dialog for free mode */}
      {isOrganizer && (
        <ConnectCourtsDialog
          open={showConnectCourts}
          onOpenChange={setShowConnectCourts}
          activity={activity}
        />
      )}
    </div>
  );
}
